import React, { useCallback, useEffect, useMemo } from 'react';

import { useHistory, useLocation } from 'react-router';

import { schemas } from '@recurrency/core-api-schema';
import { DailyWorkflowTaskResolution, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { DeleteDailyWorkflowTaskResolutionQueryParams } from '@recurrency/core-api-schema/dist/dailyWorkflow/deleteDailyWorkflowTaskResolution';
import { GetDailyWorkflowItemLocationDataResponse } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowItemLocationData';
import {
  DailyWorkflowRuleInfoDTO,
  DailyWorkflowRuleInfoDTOList,
} from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTasks';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';

import { DailyWorkflowContent } from 'pages/dailyWorkflow/DailyWorkflowContent';
import { DailyWorkflowEmptyState } from 'pages/dailyWorkflow/DailyWorkflowEmptyState';
import { DailyWorkflowFinishedState } from 'pages/dailyWorkflow/DailyWorkflowFinishedState';
import { DailyWorkflowSidePane } from 'pages/dailyWorkflow/DailyWorkflowSidePane';
import {
  DailyWorkflowSubmissionConfirmationResults,
  PlanningWorkflowSubmissionConfirmationModal,
} from 'pages/dailyWorkflow/DailyWorkflowSubmissionConfirmation';
import { DailyWorkflowPageState, ItemWithResolutionData } from 'pages/dailyWorkflow/types';
import { getWorkflowReviewProgress } from 'pages/dailyWorkflow/utils';
import { getSummaryDataFromResolutionData } from 'pages/planning/utils';
import { getPlanningListSelectionFromFilterSelection } from 'pages/purchasing/PlanningPage/modals/utils';

import { Button } from 'components/Button';
import { Container } from 'components/Container';
import { DividerLine } from 'components/DividerLine';
import { FlexSpace } from 'components/FlexSpace';
import { ConfirmModal } from 'components/Modal/ConfirmModal';
import { PageHeader } from 'components/PageHeader';
import { SearchFrameSelection } from 'components/recipes/SearchFrame/types';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { findAndUpsert } from 'utils/array';
import { showAsyncModal } from 'utils/asyncModal';
import { captureError } from 'utils/error';
import { splitIdNameStr, splitIfIdNameStr } from 'utils/formatting';
import { routes, useHashState } from 'utils/routes';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { DailyWorkflowTrackEventSource, track, TrackEvent } from 'utils/track';

import { SearchFrameHashState } from 'types/hash-state';

import { PLANNING_WORKFLOW_LIMIT } from '../../purchasing/PlanningPage/utils';

export const PlanningWorkflowPage = () => {
  const history = useHistory();
  const { activeTenant } = useGlobalApp();
  const [hashState] = useHashState<SearchFrameHashState>();
  const [workflowResponse, setWorkflowResponse] = React.useState<GetDailyWorkflowItemLocationDataResponse>();
  const [isDailyWorkflowTasksLoading, setIsDailyWorkflowTasksLoading] = React.useState(true);

  const location = useLocation<{ selection: SearchFrameSelection<SearchPlanningDTO> }>();
  const planningPageSelectedItems = useMemo(
    () => (location.state?.selection?.type === 'list' ? location.state.selection.items : []),
    [location.state],
  );

  /**
   * Workflow tasks
   */
  const defaultPlanningWorkflowDataItem = useMemo(
    () => ({
      ruleId: 'PLANNING_WORKFLOW',
      ruleName: 'Items selected from Planning',
      ruleDescription: 'Items that were selected from the Search Planning page',
      taskCount: workflowResponse?.items.length || 0,
      actionedTaskCount: 0,
      reviewedItemLocations: [],
      submittedItemLocations: [],
    }),
    [workflowResponse?.items.length],
  );

  const [workflowTasksData, setWorkflowTasksData] = React.useState<DailyWorkflowRuleInfoDTOList | undefined>({
    items: [defaultPlanningWorkflowDataItem],
    totalCount: 1,
    dailyWorkflowRunIds: ['default_value'],
  });

  const updateWorkflowTasksData = useCallback(
    (update: Partial<DailyWorkflowRuleInfoDTO>) => {
      setWorkflowTasksData((prev) => ({
        items: [
          {
            ...defaultPlanningWorkflowDataItem,
            ...prev?.items[0],
            ...update,
          },
        ],
        totalCount: 1,
        dailyWorkflowRunIds: ['default_value'],
      }));
    },
    [defaultPlanningWorkflowDataItem],
  );

  const fetchWorkflowItems = useCallback(async () => {
    setIsDailyWorkflowTasksLoading(true);
    try {
      // Get list of items from search planning filters
      const planningItems =
        planningPageSelectedItems.length > 0
          ? planningPageSelectedItems
          : (
              await getPlanningListSelectionFromFilterSelection({
                type: 'filter',
                query: hashState.query,
                filters: hashState.where,
                sortBy: hashState.sortBy,
                secondarySortBy: hashState.secondarySortBy,
                count: 0,
              })
            ).items;

      // Map items to itemLocationIDs
      const itemLocationIDs = planningItems.slice(0, PLANNING_WORKFLOW_LIMIT).map((item) => ({
        itemId: item.item_uid,
        locationId: splitIdNameStr(item.location).foreignId,
      }));

      if (itemLocationIDs.length === 0) {
        setWorkflowResponse(undefined);
        updateWorkflowTasksData({ taskCount: 0 });
        return;
      }

      // Get workflow items
      const { data: workflowItemsData } = await coreApiFetch(
        schemas.dailyWorkflow.getDailyWorkflowItemLocationDataByIDs,
        {
          bodyParams: {
            itemLocationIDs,
          },
        },
      );

      // Update workflow response and task count
      setWorkflowResponse(workflowItemsData);
      updateWorkflowTasksData({
        taskCount: workflowItemsData.items.length,
      });
    } catch (error) {
      setWorkflowResponse(undefined);
      updateWorkflowTasksData({ taskCount: 0 });
    } finally {
      setIsDailyWorkflowTasksLoading(false);
    }
  }, [
    hashState.query,
    hashState.secondarySortBy,
    hashState.sortBy,
    hashState.where,
    planningPageSelectedItems,
    updateWorkflowTasksData,
  ]);

  /**
   * Fetch workflow items
   */
  useEffect(() => {
    fetchWorkflowItems();
  }, [fetchWorkflowItems]);

  /**
   * Page state
   */
  const [pageState, setPageState] = React.useState<DailyWorkflowPageState>({
    selectedRuleId: 'PLANNING_WORKFLOW',
    selectedIndex: 0,
    resolutions: {},
    gutChecks: {},
  });

  /**
   * Resolution and submission
   */
  const [resolutionData, setResolutionData] = React.useState<ItemWithResolutionData[]>([]);

  const submittedItemLocations = workflowTasksData?.items[0].submittedItemLocations || [];

  // Filter out submitted item locations
  const filteredWorkflowResponse: GetDailyWorkflowItemLocationDataResponse = {
    items:
      workflowResponse?.items.filter(
        (item) => !submittedItemLocations.includes(`${item.itemId}|${splitIdNameStr(item.location).foreignId}`),
      ) || [],
    totalCount: workflowResponse?.totalCount || 0,
  };

  function onUpdateItemResolution(itemWithResolution: ItemWithResolutionData) {
    setResolutionData((prevResData) =>
      findAndUpsert<ItemWithResolutionData>({
        array: prevResData,
        item: itemWithResolution,
        findFn: (item: ItemWithResolutionData) =>
          item.itemId === itemWithResolution.itemId && item.locationId === itemWithResolution.locationId,
      }),
    );

    return {
      data: {
        success: true,
      },
    };
  }

  function onResetItemResolution(itemToReset: DeleteDailyWorkflowTaskResolutionQueryParams) {
    setResolutionData((prevResData) =>
      prevResData.filter((item) => !(item.itemId === itemToReset.itemId && item.locationId === itemToReset.locationId)),
    );
  }

  const onPressSendReviews = async () => {
    const result = await showAsyncModal(PlanningWorkflowSubmissionConfirmationModal, {
      isLoading: false,
      summaryData: getSummaryDataFromResolutionData(resolutionData),
    });
    if (result?.submitted) {
      handleSubmitDailyWorkflow(result);
    }
  };

  async function handleSubmitDailyWorkflow(result: DailyWorkflowSubmissionConfirmationResults) {
    const submitNotification = createSubmissionNotification({
      entityName: 'Planning Workflow',
      erpType: activeTenant?.erpType,
      expectedWaitSeconds: 30,
      submittingMessage: 'Submitting planning workflow tasks...',
      fireInProgressNotification: true,
    });
    try {
      await coreApiFetch(schemas.dailyWorkflow.submitDailyWorkflowTasksWithData, {
        pathParams: { tenantId: activeTenant?.id || '' },
        bodyParams: {
          itemLocationResolutions: resolutionData,
          bulkExcludeUntil: result.bulkExcludeUntil,
        },
      });

      submitNotification.success({
        successMessage: 'Planning workflow tasks submitted',
        description: () => 'Planning workflow tasks submitted',
      });

      track(TrackEvent.DailyWorkflow_SubmitDailyWorkflowTasks, {
        totalItems: result.totalItems,
        acceptedItems: result.accepted,
        excludedItems: result.overridden,
        flaggedItems: result.flagged,
        source: DailyWorkflowTrackEventSource.Planning,
      });

      // Send bulk flag tracking event
      if (result.flagged > 0) {
        const flaggedItems = resolutionData.filter((item) => item.resolution === DailyWorkflowTaskResolution.Flagged);

        (workflowResponse?.items || [])
          .filter((item) =>
            flaggedItems.some(
              (flaggedItem) => flaggedItem.itemId === item.itemId && flaggedItem.locationId === item.locationId,
            ),
          )
          .forEach((record) => {
            track(TrackEvent.DemandPlanning_Record_Flag, {
              itemUid: record.itemId,
              itemId: record.itemCode || '',
              itemName: record.itemName || '',
              locationId: record.locationId,
              locationName: splitIfIdNameStr(record.location)?.name || '',
              // These two are unused by the notification channel
              demandPredictability: '',
              demandPattern: '',
              flagged: true,
              flagReason:
                flaggedItems.find((fi) => fi.itemId === record.itemId && fi.locationId === record.locationId)
                  ?.resolutionData.flagReason || '',
              recordUrl:
                window.location.origin +
                routes.demandPlanning.planning({
                  where: {
                    location: [record.location],
                  },
                  query: record.itemCode,
                }),
            });
          });
      }

      // Update submitted item locations
      const newSubmittedItemLocations = resolutionData.map((item) => `${item.itemId}|${item.locationId}`);
      const updatedSubmittedItemLocations = [...submittedItemLocations, ...newSubmittedItemLocations];
      updateWorkflowTasksData({
        submittedItemLocations: updatedSubmittedItemLocations,
      });

      // Remove submitted items from resolution data local state
      setResolutionData((prevResData) =>
        prevResData.filter((item) => !updatedSubmittedItemLocations.includes(`${item.itemId}|${item.locationId}`)),
      );

      // Update selected index to account for removed items
      setPageState((prev) => ({
        ...prev,
        selectedIndex: Math.max(0, prev.selectedIndex - newSubmittedItemLocations.length),
      }));

      const confirmed = await showAsyncModal(ConfirmModal, {
        title: 'Go back to Planning?',
        content: (
          <FlexSpace direction="column" gap={16}>
            <p>
              Your planning filters will be kept for further bulk actions on these items. If you manually selected these
              items for review, those selections will not be preserved and you should complete your review within Daily
              Workflow first.
            </p>
          </FlexSpace>
        ),
        okText: 'Go to Planning',
        cancelText: 'Keep Reviewing',
      });

      if (confirmed) history.goBack();
    } catch (err) {
      submitNotification.error(err);
      captureError(err);
    }
  }

  /**
   * Review Progress
   */

  const resolutions = useMemo(() => {
    const resolutionsObject: DailyWorkflowPageState['resolutions'] = {};
    resolutionData.forEach((item) => {
      resolutionsObject[item.itemId] = { resolutionData: item.resolutionData, ruleId: 'PLANNING_WORKFLOW' };
    });
    return resolutionsObject;
  }, [resolutionData]);

  // Calculate review progress directly using utility function
  const reviewProgress = useMemo(() => {
    if (!workflowTasksData) {
      return {
        totalItemsCount: 0,
        reviewedItemsCount: 0,
        submittedItemsCount: 0,
        areAllItemsReviewed: false,
        areAllItemsSubmitted: false,
        allReviewedItems: [],
        newItemsReviewedInSessionByRule: {},
        isDailyWorkflowTasksEmpty: true,
        remainingCount: 0,
        items: {},
      };
    }

    return getWorkflowReviewProgress(workflowTasksData, resolutions);
  }, [workflowTasksData, resolutions]);

  const isEmptyWorkflowData =
    !isDailyWorkflowTasksLoading &&
    workflowTasksData?.items.reduce((acc: number, curr: DailyWorkflowRuleInfoDTO) => acc + curr.taskCount, 0) === 0 &&
    reviewProgress.submittedItemsCount === 0;

  return (
    <Container>
      <PageHeader
        title="Planning Workflow"
        subtitle={<>Planning workflow items</>}
        entity={{ kind: 'Planning' }}
        onBack={() => history.goBack()}
        headerActions={
          <Button
            type="primary"
            permissionsRequired={[
              TenantUserPermission.ActionExportMinMax,
              TenantUserPermission.ActionEditItemSupplierDetails,
            ]}
            onClick={onPressSendReviews}
          >
            Send Reviews to {activeTenant?.erpType.toUpperCase()}
          </Button>
        }
      />
      <DividerLine />

      <FlexSpace direction="row" gap={20} alignItems="flex-start">
        <DailyWorkflowSidePane
          dailyWorkflowTasks={workflowTasksData?.items || []}
          pageState={pageState}
          reviewProgress={reviewProgress}
          setSelectedRuleId={(ruleId) => {
            setPageState((prev) => ({
              ...prev,
              selectedRuleId: ruleId,
            }));
          }}
          setSelectedIndex={(index) => {
            setPageState((prev) => ({
              ...prev,
              selectedIndex: index,
            }));
          }}
        />
        {isEmptyWorkflowData && !isDailyWorkflowTasksLoading ? (
          <DailyWorkflowEmptyState />
        ) : (reviewProgress.areAllItemsReviewed || reviewProgress.areAllItemsSubmitted) &&
          !isDailyWorkflowTasksLoading ? (
          <DailyWorkflowFinishedState
            type={reviewProgress.areAllItemsSubmitted ? 'submitted' : 'reviewed'}
            onPressSendReviews={onPressSendReviews}
          />
        ) : (
          <DailyWorkflowContent
            pageState={pageState}
            setPageState={setPageState}
            dailyWorkflowResponse={filteredWorkflowResponse}
            isDailyWorkflowLoading={isDailyWorkflowTasksLoading}
            dailyWorkflowTasksData={workflowTasksData}
            ruleIdx={0}
            reloadPlanning={fetchWorkflowItems}
            onAccept={onUpdateItemResolution}
            onReset={onResetItemResolution}
            onOverrideAndExclude={onUpdateItemResolution}
            onFlag={onUpdateItemResolution}
            trackEventSource={DailyWorkflowTrackEventSource.Planning}
          />
        )}
      </FlexSpace>
    </Container>
  );
};
